import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { Check, ChevronLeft, Loader2, Clock, CalendarDays, Gift, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useInfo } from "@/context/InfoContext";
import { AddToCalendar } from "@/components/site/AddToCalendar";
import { useApi, errMsg, euro, DAY_SHORT, MONTHS_SHORT } from "@/lib/api";

const STEPS = ["Servicio", "Día", "Hora", "Tus datos", "Revisión"];

const StepBar = ({ step }) => (
  <ol className="flex gap-2 sm:gap-4 mb-10 overflow-x-auto" data-testid="booking-steps">
    {STEPS.map((s, i) => (
      <li key={s} className="flex-1 min-w-[64px]">
        <div className={`h-[2px] ${i <= step ? "bg-[#111111]" : "bg-zinc-200"} transition-colors duration-500`} />
        <p className={`mt-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase ${i === step ? "text-[#111111] font-semibold" : "text-zinc-400"}`}>
          {i + 1}. {s}
        </p>
      </li>
    ))}
  </ol>
);

const fmtDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return { wd: DAY_SHORT[(dt.getDay() + 6) % 7], d, m: MONTHS_SHORT[m - 1] };
};

const ServiceStep = ({ services, value, onPick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="booking-wizard-step-1">
    {services.map((s, i) => (
      <button key={s.id} onClick={() => onPick(s)} data-testid={`booking-service-${i}`}
        className={`text-left p-6 sm:p-8 border transition-colors duration-200 ${value?.id === s.id ? "border-[#111111] bg-[#111111] text-white" : "border-zinc-200 hover:border-[#111111]"}`}>
        <p className="text-lg font-semibold">{s.name}</p>
        <div className="mt-6 flex items-end justify-between">
          <span className={`text-sm flex items-center gap-2 ${value?.id === s.id ? "text-zinc-300" : "text-zinc-500"}`}><Clock className="w-4 h-4" />{s.duration} min</span>
          <span className="font-mono text-2xl">{euro(s.price)}</span>
        </div>
      </button>
    ))}
  </div>
);

const DayStep = ({ days, loading, value, onPick }) => {
  if (loading) return <Loading />;
  return (
    <div data-testid="booking-wizard-step-2">
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {days.map((d) => {
          const f = fmtDate(d.date);
          const ok = d.open && d.free > 0;
          return (
            <button key={d.date} disabled={!ok} onClick={() => onPick(d.date)} data-testid={`booking-day-${d.date}`}
              className={`py-3 border text-center transition-colors duration-200 ${value === d.date ? "bg-[#111111] text-white border-[#111111]" : ok ? "border-zinc-200 hover:border-[#111111]" : "border-zinc-100 text-zinc-300 cursor-not-allowed bg-zinc-50"}`}>
              <span className="block text-[10px] tracking-[0.15em] uppercase">{f.wd}</span>
              <span className="block font-mono text-xl mt-1">{f.d}</span>
              <span className="block text-[10px] uppercase">{f.m}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-zinc-500">Los días en gris están cerrados o completos. Domingos cerrado.</p>
    </div>
  );
};

const TimeStep = ({ slots, loading, value, onPick }) => {
  if (loading) return <Loading />;
  const free = slots.filter((s) => s.available);
  if (!free.length) return <p className="text-zinc-600" data-testid="booking-no-slots">No quedan horas disponibles este día. Elige otro día.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="booking-wizard-step-3">
      {slots.map((s) => (
        <button key={s.time} disabled={!s.available} onClick={() => onPick(s.time)} data-testid={`booking-time-${s.time.replace(":", "")}`}
          className={`h-12 font-mono text-sm border transition-colors duration-200 ${value === s.time ? "bg-[#111111] text-white border-[#111111]" : s.available ? "border-zinc-200 hover:border-[#111111]" : "border-zinc-100 text-zinc-300 line-through cursor-not-allowed bg-zinc-50"}`}>
          {s.time}
        </button>
      ))}
    </div>
  );
};

const Loading = () => (
  <div className="py-12 flex items-center gap-3 text-zinc-500" data-testid="booking-loading"><Loader2 className="w-5 h-5 animate-spin" /> Cargando disponibilidad…</div>
);

const Field = ({ label, error, ...p }) => (
  <label className="block">
    <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">{label}</span>
    <input {...p} className={`mt-2 w-full h-12 px-4 border bg-white outline-none focus:border-[#111111] transition-colors ${error ? "border-red-500" : "border-zinc-300"}`} />
    {error && <span className="text-xs text-red-600 mt-1 block" data-testid={`${p["data-testid"]}-error`}>{error}</span>}
  </label>
);

export const validateDetails = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Introduce tu nombre";
  if (!/^\+?[0-9 ()-]{6,20}$/.test(f.phone.trim()) || f.phone.replace(/\D/g, "").length < 9) e.phone = "Teléfono no válido";
  if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(f.email.trim())) e.email = "Correo electrónico no válido";
  return e;
};

const Summary = ({ service, date, time, form, discount }) => {
  const f = fmtDate(date);
  return (
    <dl className="border border-zinc-200 divide-y divide-zinc-200" data-testid="booking-summary">
      {[
        ["Servicio", service.name],
        ["Fecha", `${f.wd} ${f.d} ${f.m}`],
        ["Hora", time],
        ["Duración", `${service.duration} min`],
        ["Nombre", form.name],
        ["Teléfono", form.phone],
        ["Correo", form.email],
        ...(discount ? [["Recompensa", `- ${euro(discount)}`]] : []),
        ["Total", euro(service.price - discount)],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 px-5 py-4 text-sm">
          <dt className="text-zinc-500">{k}</dt>
          <dd className={`text-right ${k === "Total" ? "font-mono text-lg font-semibold" : ""}`} data-testid={`summary-${k.toLowerCase()}`}>{v}</dd>
        </div>
      ))}
    </dl>
  );
};

const Confirmation = ({ b }) => (
  <div className="max-w-xl fade-up" data-testid="booking-confirmation">
    <div className="w-14 h-14 bg-[#111111] text-white flex items-center justify-center mb-8"><Check className="w-7 h-7" /></div>
    <p className="text-xs tracking-[0.3em] uppercase text-zinc-500">Reserva registrada</p>
    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">¡Te esperamos!</h1>
    <p className="mt-4 text-zinc-600">Código de reserva: <span className="font-mono font-semibold text-[#111111]" data-testid="confirmation-code">{b.code}</span></p>
    <dl className="mt-8 border border-zinc-200 divide-y divide-zinc-200">
      {[["Servicio", b.service_name], ["Fecha", b.date_label], ["Hora", `${b.start} – ${b.end}`], ["Precio", euro(b.amount_due)], ["Nombre", b.name], ["Teléfono", b.phone], ["Correo", b.email], ["Estado", "Pendiente de confirmación por el salón"]].map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 px-5 py-4 text-sm">
          <dt className="text-zinc-500">{k}</dt><dd className="text-right" data-testid={`confirmation-${k.toLowerCase()}`}>{v}</dd>
        </div>
      ))}
    </dl>
    <p className={`mt-6 text-sm flex items-start gap-2 ${b.email_status === "sent" ? "text-zinc-700" : "text-amber-700"}`} data-testid="confirmation-email-status">
      {b.email_status === "sent" ? <Mail className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
      {b.email_status === "sent" ? `Te hemos enviado un correo de confirmación a ${b.email}.` : "Tu reserva está guardada, pero no hemos podido enviar el correo de confirmación. Guarda tu código."}
    </p>
    <AddToCalendar b={b} />
    <div className="mt-10 flex flex-wrap gap-3">
      <Show when="signed-in"><Link to="/mi-cuenta" data-testid="confirmation-account-link" className="h-12 px-6 inline-flex items-center bg-[#111111] text-white text-xs tracking-[0.2em] uppercase">Ver mis citas</Link></Show>
      <Link to="/" data-testid="confirmation-home-link" className="h-12 px-6 inline-flex items-center border border-zinc-300 text-xs tracking-[0.2em] uppercase">Volver al inicio</Link>
    </div>
  </div>
);

export default function Booking() {
  const { info } = useInfo();
  const api = useApi();
  const { user, isSignedIn } = useUser();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [days, setDays] = useState([]);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [rewards, setRewards] = useState([]);
  const [rewardId, setRewardId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (info && !service) {
      const pre = info.services.find((s) => s.id === params.get("servicio"));
      if (pre) { setService(pre); setStep(1); }
    }
  }, [info, params, service]);

  useEffect(() => {
    if (isSignedIn && user) {
      setForm((f) => ({ ...f, name: f.name || user.fullName || "", email: f.email || user.primaryEmailAddress?.emailAddress || "" }));
      api("get", "/me/rewards").then((r) => setRewards(r.rewards.filter((x) => x.status === "available"))).catch(() => {});
    }
  }, [isSignedIn, user, api]);

  useEffect(() => {
    if (!service) return;
    setLoading(true);
    api("get", `/availability/days?service_id=${service.id}`).then(setDays).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [service, api]);

  const loadSlots = (d) => {
    setLoading(true);
    return api("get", `/availability?service_id=${service.id}&date=${d}`).then((r) => setSlots(r.slots)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  };

  const discount = useMemo(() => (rewardId && service ? Math.min(15, service.price) : 0), [rewardId, service]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const b = await api("post", "/bookings", { service_id: service.id, date, time, ...form, reward_id: rewardId });
      setDone(b);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const msg = errMsg(e);
      toast.error(msg);
      if (e?.response?.status === 409) {
        setTime(null);
        await loadSlots(date);
        setStep(2);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!info) return <div className="py-32 text-center text-zinc-400">Cargando…</div>;
  if (done) return <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24"><Confirmation b={done} /></section>;

  const titles = ["Elige un servicio", "Elige un día", "Elige una hora", "Tus datos", "Revisa tu reserva"];

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20" data-testid="booking-page">
      <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-3">Reserva online</p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-10" data-testid="booking-step-title">{titles[step]}</h1>
      <StepBar step={step} />

      {step > 0 && (
        <button onClick={() => setStep(step - 1)} data-testid="booking-back-button" className="mb-6 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#111111]">
          <ChevronLeft className="w-4 h-4" /> Atrás
        </button>
      )}

      {step === 0 && <ServiceStep services={info.services} value={service} onPick={(s) => { setService(s); setDate(null); setTime(null); setStep(1); }} />}
      {step === 1 && <DayStep days={days} loading={loading} value={date} onPick={(d) => { setDate(d); setTime(null); loadSlots(d); setStep(2); }} />}
      {step === 2 && <TimeStep slots={slots} loading={loading} value={time} onPick={(t) => { setTime(t); setStep(3); }} />}
      {step === 3 && (
        <form className="space-y-5 max-w-lg" data-testid="booking-wizard-step-4" noValidate onSubmit={(e) => {
          e.preventDefault();
          const er = validateDetails(form);
          setErrors(er);
          if (!Object.keys(er).length) setStep(4);
        }}>
          <Field label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={errors.name} data-testid="booking-name-input" autoComplete="name" />
          <Field label="Teléfono" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} error={errors.phone} data-testid="booking-phone-input" autoComplete="tel" placeholder="+34 600 00 00 00" />
          <Field label="Correo electrónico" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} error={errors.email} data-testid="booking-email-input" autoComplete="email" />
          <Show when="signed-out">
            <p className="text-sm text-zinc-500">¿Quieres acumular puntos de fidelidad? <Link to="/sign-in" className="underline text-[#111111]" data-testid="booking-signin-link">Inicia sesión</Link> antes de reservar.</p>
          </Show>
          <button type="submit" data-testid="booking-details-continue" className="h-12 px-8 bg-[#111111] text-white text-xs tracking-[0.2em] uppercase">Continuar</button>
        </form>
      )}
      {step === 4 && (
        <div className="max-w-lg space-y-6" data-testid="booking-wizard-step-5">
          {rewards.length > 0 && service.price > 0 && (
            <label className="flex items-start gap-3 p-4 border border-zinc-200 cursor-pointer" data-testid="booking-reward-option">
              <input type="checkbox" checked={!!rewardId} onChange={(e) => setRewardId(e.target.checked ? rewards[0].id : null)} className="mt-1 accent-[#111111]" data-testid="booking-reward-checkbox" />
              <span className="text-sm"><Gift className="w-4 h-4 inline mr-1" /> Usar mi recompensa <span className="font-mono">{rewards[0].code}</span> (corte gratis hasta 15 €)</span>
            </label>
          )}
          <Summary service={service} date={date} time={time} form={form} discount={discount} />
          <p className="text-xs text-zinc-500 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Podrás cancelar hasta {info.settings.cancel_hours} h antes de la cita.</p>
          <button onClick={submit} disabled={submitting} data-testid="booking-confirm-submit-button" className="w-full h-14 bg-[#111111] text-white text-xs tracking-[0.25em] uppercase disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar reserva
          </button>
        </div>
      )}
    </section>
  );
}
